import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../../components/utils/Copyable';
import ConfirmationModal from '../../../../../../components/modals/ConfirmationModal';
import TopBar from '../../../../../../components/utils/TopBar';
import NA from '../../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { 
  GetCCCurrenciesBIZcoreCC,
  SeedCCCurrenciesBIZcoreCC,
  WipeCCCurrenciesBIZcoreCC
} from '../../../../../../redux/actions/business/core/currencyCloudActions';

import CurrenciesModal from './CurrenciesModal';

import global from "../../../../../../global.scss";

const CurrenciesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.cc"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currenciesModalOpen, setCurrenciesModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [currenciesList, setCurrenciesList] = useState([]);
  const [currenciesTotal, setCurrenciesTotal] = useState(0);

  const [currenciesMenuOpen, setCurrenciesMenuOpen] = useState(false);
  const [currenciesMenuAnchor, setCurrenciesMenuAnchor] = useState(null);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  const [seedConfirmationModalOpen, setSeedConfirmationModalOpen] = useState(false);
  const [wipeConfirmationModalOpen, setWipeConfirmationModalOpen] = useState(false);

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Name",
      sort: "name",
    },
    {
      label: "Code",
      sort: "code",
    },
    {
      label: "Symbol",
      sort: "symbol",
    },
    {
      label: "Decimals",
      sort: "decimal_position",
    },
    {
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null
    }
  ];

  useEffect(() => {
    handleGetCurrencies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatCurrenciesList(state.currenciesList.records || []);
      setCurrenciesTotal(state.currenciesList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatCurrenciesList = (list) => {
    setCurrenciesList(list)
  };

  async function handleGetCurrencies(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    await dispatch(GetCCCurrenciesBIZcoreCC(page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
  };

  async function handleSeed(){
    setSeedConfirmationModalOpen(false);
    await dispatch(SeedCCCurrenciesBIZcoreCC(setLoading));
    handleGetCurrencies();
  };

  // NOTE: wired up and working, but intentionally left disabled in the UI —
  // these ids are FK targets for the payment/collection tables, so a wipe is
  // only ever appropriate on a throwaway environment. Re-enable deliberately.
  async function handleWipe(){
    setWipeConfirmationModalOpen(false);
    await dispatch(WipeCCCurrenciesBIZcoreCC(setLoading));
    handleGetCurrencies();
  };

  return (
    <Box>

      <CurrenciesModal 
        open={currenciesModalOpen}
        setOpen={setCurrenciesModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetCurrencies}
        />

      <ConfirmationModal
        open={seedConfirmationModalOpen}
        setOpen={setSeedConfirmationModalOpen}
        title={"Seed the standard currency list?"}
        message={"Inserts the 35 standard CurrencyCloud currencies. Ids are pinned, so payment and collection references stay intact and rows already present are refreshed in place."}
        onConfirm={handleSeed}
        />

      <ConfirmationModal
        open={wipeConfirmationModalOpen}
        setOpen={setWipeConfirmationModalOpen}
        title={"Wipe all currencies?"}
        message={"Permanently deletes every row in the CurrencyCloud currencies table. The server refuses this while any payment/collection currency or payment purpose still references it."}
        onConfirm={handleWipe}
        />

      <TopBar
        title={"Currencies"}
        search={search}
        searchPlaceholder={"Search id, name, code or symbol"}
        onSearchChange={handleSearchChange}
        button2Text={"Seed all currencies"}
        button2OnClick={() => {
          setSeedConfirmationModalOpen(true);
        }}
        button3Text={"Wipe all"}
        button3Disabled={true}
        button3OnClick={() => {
          setWipeConfirmationModalOpen(true);
        }}
        button1Text={"Add Currency"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add Currency");
          setCurrenciesModalOpen(!currenciesModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          currenciesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No currency found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                currenciesList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.name || <NA />}</TableCell>
                    <TableCell>{row.code || <NA />}</TableCell>
                    <TableCell>{row.symbol || <NA />}</TableCell>
                    <TableCell>{row.decimal_position || <NA />}</TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setCurrenciesMenuAnchor(e.currentTarget);
                          setCurrenciesMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.name || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={currenciesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={currenciesMenuOpen}
        anchorEl={currenciesMenuAnchor}
        onClose={() => setCurrenciesMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setCurrenciesMenuOpen(false);
            setCurrenciesModalOpen(!currenciesModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default CurrenciesPage;